import { useMemo, useState, type ReactNode } from 'react';
import { Filter, Search, X } from 'lucide-react';
import { Toolbar } from '@vnetwork/ui/layout';
import {
  DataTable,
  EmptyState,
  FilterBuilder,
  Pagination,
  type ColumnDef,
  type FilterCondition,
} from '@vnetwork/ui/data';
import { paginationView } from '../../pagination-summary';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Chip,
  Input,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@vnetwork/ui/primitives';
import type { BadgeTone, ChipTone } from '@vnetwork/ui/primitives';
import { useServiceSlas, type CustomerServiceRow } from '@/api/customer-sla';
import { TableLink } from '@/components/table-link';
import { EntityLink } from '@/components/entity-link';
import { humanize, serviceTone } from '../status-tones';
import { TabLoading } from '../tab-states';
import { formatDuration } from './format-duration';
import { buildSlaFilterFields, matchesSlaConditions, type SlaRow } from './service-sla-filters';

/** SLA tier slug → Chip tone (bronze/silver/gold/platinum). */
function tierTone(tier?: string): ChipTone {
  switch (tier) {
    case 'platinum':
    case 'gold':
      return 'warning';
    case 'silver':
      return 'info';
    case 'bronze':
      return 'neutral';
    default:
      return 'primary';
  }
}

const muted = (text: ReactNode) => (
  <span style={{ color: 'var(--vn-fg-muted)' }}>{text}</span>
);

const columns: ColumnDef<SlaRow, unknown>[] = [
  {
    id: 'service',
    header: 'Service',
    size: 340,
    cell: ({ row }) => {
      const s = row.original.service;
      return (
        <div style={{ minWidth: 240 }}>
          <EntityLink
            to={`/services/${s.service_id}`}
            id={s.service_id}
            name={s.service_name}
          />
          {s.product_group_name ? (
            <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
              {s.product_group_name}
            </div>
          ) : null}
        </div>
      );
    },
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <Badge tone={serviceTone(row.original.service.service_status) as BadgeTone} appearance="subtle" size="sm">
        {humanize(row.original.service.service_status)}
      </Badge>
    ),
  },
  {
    id: 'sla',
    header: 'SLA',
    cell: ({ row }) => {
      const { sla } = row.original;
      if (!sla) return muted('No SLA');
      const tier = sla.template_tier?.sla_tier;
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
          {tier ? <Chip size="sm" tone={tierTone(tier)}>{humanize(tier)}</Chip> : null}
          <Badge tone={sla.is_active ? 'success' : 'neutral'} appearance="subtle" size="sm">
            {sla.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </div>
      );
    },
  },
  {
    id: 'uptime',
    header: () => <div style={{ textAlign: 'right' }}>Uptime</div>,
    cell: ({ row }) => {
      const g = row.original.sla?.effective_uptime_guarantee;
      return <div style={{ textAlign: 'right' }}>{g != null ? `${g}%` : muted('—')}</div>;
    },
  },
  {
    id: 'business_hours',
    header: 'Coverage',
    cell: ({ row }) => {
      const sla = row.original.sla;
      if (!sla) return muted('—');
      return sla.effective_business_hours_only ? 'Business hours' : '24/7';
    },
  },
  {
    id: 'response',
    header: () => <div style={{ textAlign: 'right' }}>Response</div>,
    cell: ({ row }) => (
      <div style={{ textAlign: 'right' }}>
        {row.original.sla ? formatDuration(row.original.sla.template_default_response_time) : muted('—')}
      </div>
    ),
  },
  {
    id: 'handle',
    header: () => <div style={{ textAlign: 'right' }}>Handle</div>,
    cell: ({ row }) => (
      <div style={{ textAlign: 'right' }}>
        {row.original.sla ? formatDuration(row.original.sla.template_default_resolution_time) : muted('—')}
      </div>
    ),
  },
  {
    id: 'rules',
    header: () => <div style={{ textAlign: 'right' }}>Rules</div>,
    cell: ({ row }) => {
      const n = row.original.sla?.sla_rules?.length ?? 0;
      return <div style={{ textAlign: 'right' }}>{n > 0 ? n : muted('—')}</div>;
    },
  },
  {
    id: 'action',
    header: '',
    cell: ({ row }) => (
      <TableLink to={`/services/${row.original.service.service_id}?tab=sla`}>
        {row.original.sla ? 'View SLA' : 'Configure SLA'}
      </TableLink>
    ),
  },
];

/**
 * Section 3 — the per-service SLA table. Takes the customer's services, fans out
 * `/services/:id/sla` (via `useServiceSlas`), and renders one compact row per
 * service (tier / uptime / coverage / response / handle / rules + View/Configure
 * SLA). Replaces the earlier cards grid for a tidier, denser layout.
 */
export function ServiceSlaGrid({ services }: { services: CustomerServiceRow[] }) {
  const serviceIds = services.map((s) => s.service_id);
  const { byServiceId, isLoading } = useServiceSlas(serviceIds);
  const [query, setQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [conditions, setConditions] = useState<FilterCondition[]>([]);

  const rows: SlaRow[] = useMemo(
    () =>
      services.map((service) => ({ service, sla: byServiceId.get(service.service_id) ?? null })),
    [services, byServiceId],
  );

  const filterFields = useMemo(() => buildSlaFilterFields(rows), [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (!matchesSlaConditions(r, conditions)) return false;
      if (!q) return true;
      const hay = `${r.service.service_name} ${r.service.service_id} ${r.service.product_group_name ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, query, conditions]);

  const appliedFieldCount = new Set(conditions.map((c) => c.field)).size;

  if (isLoading) return <TabLoading rows={6} />;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Service SLAs ({services.length})</CardTitle>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      <Toolbar
        style={{ marginBottom: 0 }}
        start={
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search services…"
            startSlot={<Search />}
            style={{ maxWidth: 320 }}
          />
        }
        end={
          <>
            {appliedFieldCount ? (
              <Button variant="ghost" startIcon={<X size={15} />} onClick={() => setConditions([])}>
                Clear
              </Button>
            ) : null}
            <Popover open={filterOpen} onOpenChange={setFilterOpen}>
              <PopoverTrigger asChild>
                <Button variant="secondary" startIcon={<Filter size={15} />}>
                  Filters {appliedFieldCount ? <Badge tone="primary">{appliedFieldCount}</Badge> : null}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" style={{ padding: 0 }}>
                <FilterBuilder
                  fields={filterFields}
                  defaultValue={conditions.length ? conditions : undefined}
                  onApply={(next) => {
                    setConditions(next.filter((c) => c.value !== ''));
                    setFilterOpen(false);
                  }}
                  onClose={() => setFilterOpen(false)}
                />
              </PopoverContent>
            </Popover>
          </>
        }
      />
      <DataTable
        columns={columns}
        data={filtered}
        enableSorting={false}
        getRowId={(r) => String(r.service.service_id)}
        emptyState={
          <EmptyState title="No services" description="No services match your search or filter." />
        }
        footer={(() => {
          // Services are loaded in one page (client-filtered) — a single-page summary.
          const view = paginationView(1, Math.max(filtered.length, 1), filtered.length, filtered.length, 1);
          return <Pagination page={1} pageCount={view.pageCount} onPageChange={() => {}} summary={view.summary} />;
        })()}
      />
      </CardBody>
    </Card>
  );
}
